import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  forwardRef,
  HostListener,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChange,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { identity, prop, propEq } from 'ramda';

import { ButtonComponent } from '../../components';
import { valueAccessorFactory, WithDisabled } from '../../helpers';
import { ComponentStyle } from '../../models';
import { isFalse } from '../../util';

type IsChecked = (buttons: SelectButtonDirective) => boolean;
const isChecked: IsChecked = prop('checked');

const valueEq = propEq('value');

@Component({
  selector: 'pcs-select-button-group',
  template: '<ng-content></ng-content>',
  providers: [
    valueAccessorFactory(SelectButtonGroupComponent)
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectButtonGroupComponent implements ControlValueAccessor, WithDisabled, AfterContentInit, OnChanges {
  @Input() value: any;
  @Input() disabled;
  @Input() required = false;

  @Output() readonly changed = new EventEmitter<any>();

  @ContentChildren(forwardRef(() => SelectButtonDirective)) selectButtons: QueryList<SelectButtonDirective>;

  propagateChange = identity;
  propagateTouched = identity;

  private selected: SelectButtonDirective;

  ngAfterContentInit(): void {
    const selected = this.selectButtons.find(isChecked);

    if (selected) {
      selected.checked = true;
      this.selected = selected;
    }
  }

  ngOnChanges({value: valueChange, disabled: disabledChange}: SimpleChanges): void {
    if (valueChange && valueChange.currentValue !== valueChange.previousValue) {
      this.selectByValue(valueChange.currentValue);
    }
    if (disabledChange && disabledChange.currentValue !== disabledChange.previousValue) {
      this.setDisabledState(disabledChange.currentValue);
    }
  }

  writeValue(value: any) {
    this.value = value;
    this.selectByValue(this.value);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (this.selectButtons) {
      this.selectButtons.forEach(button => button.setDisabled(this.disabled));
    }
  }

  select(selectedComponent: SelectButtonDirective, select: boolean) {
    if (this.selected && selectedComponent.checked) {
      this.selected.checked = false;
    }

    this.selected = select ? selectedComponent : null;
  }

  isPrechecked(selectButton: SelectButtonDirective) {
    return valueEq(this.value, selectButton);
  }

  isSelected(selectButton: SelectButtonDirective) {
    return isChecked(selectButton) && valueEq(this.value, selectButton);
  }

  emitChangeEvent() {
    const value = this.selected?.value;
    this.propagateChange(value);
    this.changed.emit(value);
  }

  private selectByValue(value) {
    if (this.selectButtons) {
      this.clearSelection();
      this.selectValue(value);
    }
  }

  private clearSelection() {
    this.selectButtons.forEach(selectButton => selectButton.checked = false);
  }

  private selectValue(value) {
    const correspondingOption = this.selectButtons.find(valueEq(value));

    if (correspondingOption) {
      correspondingOption.checked = true;
    }
  }

}


@Directive({
  selector: '[pcsSelectButton]'
})
export class SelectButtonDirective implements OnInit, OnDestroy {
  @Input() value: any;

  @Input()
  get checked(): boolean {
    return this._checked;
  }

  set checked(value: boolean) {
    if (this._checked !== value) {
      this._checked = value;
      this.selectButtonGroup.select(this, this._checked);
      this.setButtonStyle();
    }
  }

  @Output() readonly changed = new EventEmitter<{ value; checked }>();

  private _checked = false;

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    @Inject(SelectButtonGroupComponent) private selectButtonGroup: SelectButtonGroupComponent,
    @Inject(ButtonComponent) private button: ButtonComponent
  ) {
  }

  ngOnInit(): void {
    if (this.selectButtonGroup.isPrechecked(this)) {
      this.checked = true;
      this.setButtonStyle();
    }
  }

  @HostListener('click')
  onClick(event) {
    if (this.selectionChangeAllowed()) {
      this._checked = !this._checked;

      this.selectButtonGroup.select(this, this._checked);
      this.selectButtonGroup.emitChangeEvent();
      this.selectButtonGroup.propagateTouched(event);
      this.setButtonStyle();

      this.changed.emit({value: this.value, checked: this.checked});
    }
  }

  setDisabled(disabled: boolean) {
    this.button.disabled = disabled;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.selectButtonGroup.isSelected(this)) {
      this.selectButtonGroup.select(this, false);
    }
  }

  private setButtonStyle() {
    this.button.buttonStyle = this.getCheckedButtonStyle();
    this.button.ngOnChanges({buttonStyle: new SimpleChange(this.button.buttonStyle, this.getCheckedButtonStyle(), false)});
    this.cdr.markForCheck();
  }

  private getCheckedButtonStyle() {
    return this.checked ? ComponentStyle.Primary : ComponentStyle.Default;
  }

  private selectionChangeAllowed() {
    return isFalse(this.button.disabled) && (
      !this.selectButtonGroup.required || isFalse(this.selectButtonGroup.isSelected(this))
    );
  }
}
